import { InterfaceType } from 'frontend/_setup/setupContainer/constants/InterfaceType'
import { ToastAction } from 'frontend/_stores/toast-store'
import { useDateFormat } from 'frontend/common/composables/useDateFormat'
import { useContainer } from 'frontend/container/composables/useContainer'
import { IDataModelTypeService } from 'frontend/dataModels/services/DataModelTypeService/types/IDataModelTypeService'
import { serializeReportDateFormat } from 'frontend/common/date-format/serialize-report-date-format'
import { ReportsService } from 'frontend/reports/reports-service'
import { DataModelType } from 'frontend/roles/enum/DataModelType'
import { computed, inject, type Ref } from 'vue'

export function useDataModelReport(
  dataModelType: DataModelType,
  displayedColumnNames: Ref<string[]>,
  providedExportableType?: string,
) {
  const { container } = useContainer()
  const dataModelTypeService = container.get<IDataModelTypeService>(
    InterfaceType.services.DataModelTypeService,
  )
  const reportsService = new ReportsService()
  const $info = inject<ToastAction>('$info')
  const $error = inject<ToastAction>('$error')
  const { selectedTimeFormat, selectedDateFormat } = useDateFormat()

  const reportErrorHandler = err => {
    console.error('ERROR CREATING A REPORT', err)
    $error({
      title: 'Could not generate the report',
      message: 'Please try again later or let us know using feedback',
    })
  }

  const exportableType = computed(() => {
    return providedExportableType || dataModelTypeService.getExportableType(dataModelType)
  })

  function generateReport(filterQuery) {
    const reportDateFormat = serializeReportDateFormat(selectedTimeFormat.value, selectedDateFormat.value)
    const { filters, prefilters, sort, batchActions, groupedView } = filterQuery
    const payload: {
      exportable_type: string
      sorts?: string | string[]
      columns: string[]
      filters?: Record<string, unknown>
      date_format: string
      groupedView?: boolean
    } = {
      exportable_type: exportableType.value,
      sorts: (sort || '').constructor == String ? [sort] : sort,
      columns: [...displayedColumnNames.value],
      date_format: reportDateFormat,
      groupedView: groupedView,
    }
    if (!batchActions?.allChecked && Object.values(batchActions?.checkedItems || {}).length) {
      payload.filters = {
        id_in: Object.values(batchActions.checkedItems).map(el => String(el).split(':')[0]),
      }
    } else {
      payload.filters = { ...(prefilters || {}), ...(filters || {}) }
    }

    reportsService.save({ data: payload, errorHandler: reportErrorHandler }).then(() => {
      $info({
        title: 'The report is being generated...',
        message:
          'You will be notified once it is finished or you can see progress in "Reports" module',
        showSpinner: true,
      })
    })
  }

  return {
    generateReport,
    exportableType,
  }
}
